import {
    getAllOrderTicketsFromCollection,
    OrderTicketCollection,
} from '@/models/orderTicket';
import { isValid } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { Gender } from '@/models/general';

// Column mapping for user-friendly names
export const columnMapping = {
    // User-friendly column names -> internal field names
    Title: 'title',
    'First name': 'firstname',
    'Last name': 'lastname',
    'Date of birth': 'birthdate',
    'Ticket name': 'ticketName',
    'Person name': 'personName',
} as const;

export const dataImportColumns = [
    'title',
    'firstname',
    'lastname',
    'birthdate',
    'ticketName',
    'personName',
] as const;

// Helper function to normalize column names for flexible matching
const normalizeColumnName = (columnName: string): string => {
    return columnName
        .trim()
        .toLowerCase()
        .replace(/[\s_-]+/g, '')
        .replace(/[^\w]/g, '');
};

// Function to map user column names to internal field names
export const mapColumnName = (userColumnName: string): string | null => {
    // First try exact match
    if (userColumnName in columnMapping) {
        return columnMapping[userColumnName as keyof typeof columnMapping];
    }

    // Then try case-insensitive match
    const userColumnLower = userColumnName.toLowerCase();
    for (const [key, value] of Object.entries(columnMapping)) {
        if (key.toLowerCase() === userColumnLower) {
            return value;
        }
    }

    // Finally try normalized match (without spaces, underscores, etc.)
    const normalizedUserColumn = normalizeColumnName(userColumnName);
    for (const [key, value] of Object.entries(columnMapping)) {
        if (normalizeColumnName(key) === normalizedUserColumn) {
            return value;
        }
    }

    return null;
};

const titleValues = ['mr', 'mrs', 'ms', 'herr', 'frau'] as const;

type TitleValue = (typeof titleValues)[number];

const normalizeTitleString = (str: string) =>
    str.trim().toLowerCase().replaceAll('.', '');

const mapTitleToGender = (title: string): Gender => {
    const mailTitles = ['mr', 'herr'] as TitleValue[];

    const normalizedTitle = title.toLowerCase().replaceAll('.', '');
    if (mailTitles.includes(normalizedTitle as TitleValue)) {
        return Gender.MALE;
    }

    return Gender.FEMALE;
};

export type UploadedImportRow = {
    ticketName: string;
    personName: string;
    title: (typeof titleValues)[number];
    firstname: string;
    lastname: string;
    birthdate: string; // YYYY-MM-DD format
};

export type ValidatedImportRow = {
    productId: string;
    ticketName: string;
    personName: string;
    title: Gender;
    firstname: string;
    lastname: string;
    birthdate: string; // YYYY-MM-DD format
};

export const useParseFileData = () => {
    const { t } = useTranslation();
    const parseXlsxData = useParseXlsxData();

    return (file: File): Promise<UploadedImportRow[]> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = async (e) => {
                try {
                    const data = e.target?.result;

                    if (!data) {
                        throw new Error(
                            t('ticketOrder.xlsxImport.errors.failedToReadFile')
                        );
                    }

                    let parsedData: UploadedImportRow[];

                    if (
                        file.name.endsWith('.xlsx') ||
                        file.name.endsWith('.xls')
                    ) {
                        parsedData = await parseXlsxData(data as ArrayBuffer);
                    } else {
                        throw new Error(
                            t(
                                'ticketOrder.xlsxImport.errors.unsupportedFileFormat'
                            )
                        );
                    }

                    resolve(parsedData);
                } catch (error) {
                    reject(error);
                }
            };

            reader.onerror = () => {
                reject(
                    new Error('ticketOrder.xlsxImport.errors.failedToReadFile')
                );
            };

            reader.readAsArrayBuffer(file);
        });
    };
};

export const useParseXlsxData = () => {
    const { t } = useTranslation();

    return async (buffer: ArrayBuffer): Promise<UploadedImportRow[]> => {
        // Loaded on demand: xlsx is large and only needed for this import
        const XLSX = await import('xlsx');
        const workbook = XLSX.read(buffer, { type: 'array' });
        const sheetName = workbook.SheetNames[0];

        if (!sheetName) {
            throw new Error(t('ticketOrder.xlsxImport.errors.xlsxNoSheet'));
        }

        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        if (jsonData.length < 2) {
            throw new Error(
                t('ticketOrder.xlsxImport.errors.xlsxHeaderRequired')
            );
        }

        const headers = (jsonData[0] as string[]).map((h) => String(h).trim());

        // Map user-friendly headers to internal field names
        const mappedHeaders = headers.map((header) => mapColumnName(header));

        // Check if all required fields are present after mapping
        const missingFields = dataImportColumns.filter(
            (expectedHeader) => !mappedHeaders.includes(expectedHeader)
        );

        if (missingFields.length > 0) {
            throw new Error(
                t('ticketOrder.xlsxImport.errors.missingHeaders', {
                    headers: missingFields.join(', '),
                })
            );
        }

        const data: UploadedImportRow[] = [];

        for (let i = 1; i < jsonData.length; i++) {
            const rowValues = jsonData[i] as string[];

            if (!rowValues || rowValues.length === 0) {
                continue; // Skip empty rows
            }

            const row: Record<string, string> = {};
            headers.forEach((header, index) => {
                const mappedFieldName = mapColumnName(header);
                if (mappedFieldName) {
                    row[mappedFieldName] = rowValues[index]
                        ? String(rowValues[index]).trim()
                        : '';
                }
            });

            data.push(row as UploadedImportRow);
        }

        return data;
    };
};

export const useValidateImportData = () => {
    const { t } = useTranslation();

    return (
        data: UploadedImportRow[],
        availableCollections: OrderTicketCollection[]
    ): ValidatedImportRow[] => {
        const availableTicketNameProductName = new Map<string, string>();
        const allTickets = availableCollections
            .map(getAllOrderTicketsFromCollection)
            .flat();

        allTickets.forEach((ticket) => {
            ticket.persons.forEach((person) => {
                if (person.name.en && ticket.name.en) {
                    availableTicketNameProductName.set(
                        `${ticket.name.en} - ${person.name.en}`,
                        ticket.productId
                    );
                }
                availableTicketNameProductName.set(
                    `${ticket.name.de} - ${person.name.de}`,
                    ticket.productId
                );
            });
        });

        return data.map<ValidatedImportRow>(
            (row, index): ValidatedImportRow => {
                // Validate gender

                const normalizedTitle = normalizeTitleString(row.title);
                if (!titleValues.includes(normalizedTitle as TitleValue)) {
                    throw new Error(
                        t('ticketOrder.xlsxImport.errors.genderInvalid', {
                            row: index + 2,
                        })
                    );
                }

                // Validate productId exists
                if (
                    !availableTicketNameProductName.has(
                        `${row.ticketName} - ${row.personName}`
                    )
                ) {
                    throw new Error(
                        t(
                            'ticketOrder.xlsxImport.errors.wrongTicketNamePersonName',
                            {
                                row: index + 2,
                                ticketName: row.ticketName,
                                personName: row.personName,
                            }
                        )
                    );
                }

                // Validate birthdate format
                const isValidDate = isValid(new Date(row.birthdate));
                if (!isValidDate) {
                    throw new Error(
                        t('ticketOrder.xlsxImport.errors.birthdateInvalid', {
                            row: index + 2,
                        })
                    );
                }

                // Validate required fields
                if (!row.firstname.trim() || !row.lastname.trim()) {
                    throw new Error(
                        t('ticketOrder.xlsxImport.errors.nameRequired', {
                            row: index + 2,
                        })
                    );
                }

                const {
                    personName,
                    title,
                    firstname,
                    lastname,
                    ticketName,
                    birthdate,
                } = row;

                return {
                    personName: personName,
                    productId:
                        availableTicketNameProductName.get(
                            `${row.ticketName} - ${row.personName}`
                        ) || '',
                    title: mapTitleToGender(title),
                    firstname,
                    lastname,
                    ticketName,
                    birthdate,
                };
            }
        );
    };
};
